import React, { useState, useEffect, useMemo } from 'react';
import './App_fixed.css';
import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS, ALBERTA_CITIES } from './data/restaurants';
import { useAuth } from './context/AuthContext';
import { apiFetch } from './lib/api';
import AuthModal from './components/AuthModal';
import ProfileView from './components/ProfileView';
import ItineraryView from './components/ItineraryView';
import SpinWheel from './components/SpinWheel';
import ItineraryWizard from './components/ItineraryWizard';
import Footer from './components/Footer';
import { generateItinerary } from './lib/generateItinerary';
import { getRestaurantImage } from './lib/foodImages';
import { FOOD_TRAILS } from './data/foodTrails';
import { isTrending, isNew } from './data/trending';

// Full Culinera Website - Original content + Fixed wizard (with original colors and adsense)
const CulinaraApp = () => {
  const { user, isAuthenticated, googleExchange, refresh } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showRestaurants, setShowRestaurants] = useState(false);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [cityFilter, setCityFilter] = useState('All');
  const [priceFilter, setPriceFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [trendingOnly, setTrendingOnly] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [savedTrailIds, setSavedTrailIds] = useState(new Set());
  const [adDismissed, setAdDismissed] = useState({ top: false, mid: false, bottom: false });
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showPremiumSuccess, setShowPremiumSuccess] = useState(false);
  const [showPremiumPricing, setShowPremiumPricing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [generatedItinerary, setGeneratedItinerary] = useState(null);
  const [savedItineraryId, setSavedItineraryId] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [tripPicks, setTripPicks] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('culinera_trip_picks') || '[]')); }
    catch { return new Set(); }
  });
  const [communityTrucks, setCommunityTrucks] = useState([]);
  const [dynamicRestaurants, setDynamicRestaurants] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [toast, setToast] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Premium status detection
  const checkPremiumStatus = () => {
    // Account-level premium (admins are auto-premium) takes precedence,
    // then a successful Stripe return / locally-stored flag.
    if (user?.is_premium) return true;
    const urlParams = new URLSearchParams(window.location.search);
    const isFromStripe = urlParams.get('success') === 'true' || 
                       window.location.pathname.includes('/success') ||
                       localStorage.getItem('culinara_premium') === 'true';
    
    return isFromStripe;
  };
  
  const isPremiumUser = checkPremiumStatus();
  
  // Free usage tracking (only for non-premium users)
  const getFreeUsageCount = () => {
    if (isPremiumUser) return 0; // Premium users don't count usage
    return parseInt(localStorage.getItem('culinara_free_usage') || '0');
  };
  
  const incrementFreeUsage = () => {
    if (isPremiumUser) return; // Premium users don't increment
    const current = getFreeUsageCount();
    localStorage.setItem('culinara_free_usage', (current + 1).toString());
  };
  
  const getRemainingFreeItineraries = () => {
    if (isPremiumUser) return 999; // Premium = unlimited
    return Math.max(0, 3 - getFreeUsageCount());
  };
  
  const activatePremium = () => {
    localStorage.setItem('culinara_premium', 'true');
    setShowPremiumSuccess(true);
  };
  
  // Handle OAuth callback + stripe success on mount
  useEffect(() => {
    // 1) Google Auth callback — session_id lives in URL fragment
    const hash = window.location.hash || '';
    if (hash.includes('session_id=')) {
      const sid = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
      if (sid) {
        googleExchange(sid)
          .then(() => {
            window.history.replaceState({}, document.title, window.location.pathname);
            setToast('Signed in successfully');
          })
          .catch((e) => {
            console.error('Google session exchange failed', e);
            setToast('Sign-in failed. Please try again.');
          });
        return; // don't process stripe success this run
      }
    }
    // 2) Stripe success return
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      localStorage.setItem('culinara_premium', 'true');
      setShowPremiumSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [googleExchange]);

  // Deep-link: "Plan this trip" CTAs on SEO landing pages open the wizard
  // pre-filled with a starting city (and optional cuisines), e.g.
  // /?view=wizard&city=Calgary&cuisines=Farm-to-Table,Japanese
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'restaurants') {
      setShowRestaurants(true);
      return;
    }
    if (view !== 'wizard') return;
    const city = params.get('city');
    const cuisines = params.get('cuisines');
    if (city || cuisines) {
      setFormData((prev) => ({
        ...prev,
        ...(city ? { startingLocation: city } : {}),
        ...(cuisines ? { cuisineTypes: cuisines.split(',').map((c) => c.trim()).filter(Boolean) } : {}),
      }));
    }
    if (getRemainingFreeItineraries() > 0) {
      setShowWizard(true);
      setCurrentStep(1);
    } else {
      setShowPremiumPricing(true);
    }
  }, []);

  // Load favorites whenever the user changes (login/logout)
  useEffect(() => {
    if (!isAuthenticated) { setFavoriteIds(new Set()); setPendingSubmissions([]); setSavedTrailIds(new Set()); return; }
    apiFetch('/api/favorites')
      .then((list) => setFavoriteIds(new Set((list || []).map((f) => f.restaurant_id))))
      .catch(() => {});
    apiFetch('/api/saved-trails')
      .then((list) => setSavedTrailIds(new Set((list || []).map((s) => s.trail_id))))
      .catch(() => {});
    // Admin: try to fetch pending submissions; ignore 403 if not admin
    apiFetch('/api/admin/submissions?status=pending')
      .then((list) => setPendingSubmissions(list || []))
      .catch(() => setPendingSubmissions([]));
  }, [isAuthenticated]);

  // Load community-approved trucks once on mount (public endpoint)
  useEffect(() => {
    apiFetch('/api/community-trucks')
      .then((list) => setCommunityTrucks(list || []))
      .catch(() => setCommunityTrucks([]));
  }, []);

  // Load dynamically-added (scraped + admin-approved) restaurants from the API.
  // These merge into the directory automatically — no rebuild/redeploy needed.
  useEffect(() => {
    apiFetch('/api/restaurants?source=scraper')
      .then((list) => setDynamicRestaurants(list || []))
      .catch(() => setDynamicRestaurants([]));
  }, []);

  const approveSubmission = async (id) => {
    try {
      await apiFetch(`/api/admin/submissions/${id}/approve`, { method: 'POST' });
      setPendingSubmissions((s) => s.filter((x) => x.id !== id));
      // Refresh public trucks so the new one shows immediately
      apiFetch('/api/community-trucks').then((list) => setCommunityTrucks(list || []));
      setToast('Approved — truck is now live in the directory');
    } catch (e) { setToast(e.message); }
  };

  const rejectSubmission = async (id) => {
    try {
      await apiFetch(`/api/admin/submissions/${id}/reject`, { method: 'POST' });
      setPendingSubmissions((s) => s.filter((x) => x.id !== id));
      setToast('Submission rejected');
    } catch (e) { setToast(e.message); }
  };

  // Auto-dismiss toasts
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Filtered restaurants list (used on the Restaurants page) — includes food trucks
  const filteredRestaurants = useMemo(() => {
    const featured = communityTrucks.filter((t) => t.is_featured);
    const nonFeatured = communityTrucks.filter((t) => !t.is_featured);
    const combined = [
      ...featured.map((t) => ({ ...t, isFoodTruck: t.type !== 'restaurant', isCommunity: true, isFeatured: true, neighborhood: 'Featured', tags: ['Featured', 'Community Submitted'] })),
      ...dynamicRestaurants.map((t) => ({ ...t, isFoodTruck: t.type === 'food_truck', isCommunity: true, neighborhood: t.neighborhood || (t.type === 'food_truck' ? 'Food Truck' : 'New on Culinera'), tags: (t.tags && t.tags.length) ? t.tags : ['New on Culinera'] })),
      ...ALBERTA_RESTAURANTS,
      ...ALBERTA_FOOD_TRUCKS.map((t) => ({ ...t, isFoodTruck: true, neighborhood: t.neighborhood || 'Food Truck' })),
      ...nonFeatured.map((t) => ({ ...t, isFoodTruck: t.type !== 'restaurant', isCommunity: true, neighborhood: 'Community-Submitted', tags: ['Community Submitted', 'New'] })),
    ];
    return combined.filter((r) => {
      const cityMatch = cityFilter === 'All' || r.city === cityFilter;
      const priceMatch = priceFilter === 'All' || r.price === priceFilter;
      const q = searchQuery.trim().toLowerCase();
      const searchMatch =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.cuisine.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q));
      const trendingMatch = !trendingOnly || isTrending(r.id);
      return cityMatch && priceMatch && searchMatch && trendingMatch;
    });
  }, [cityFilter, priceFilter, searchQuery, trendingOnly, communityTrucks, dynamicRestaurants]);

  const scrollToRestaurants = () => {
    setShowRestaurants(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showRestaurantList = () => {
    setShowRestaurants(true);
  };

  const showLearnMoreDemo = () => {
    setShowLearnMore(true);
  };

  const showPremiumModal = () => {
    // Open the pricing modal first so users see benefits & price clearly before Stripe
    setShowPremiumPricing(true);
  };

  const proceedToStripeCheckout = () => {
    const stripeLink = 'https://buy.stripe.com/cNi8wPbVaeCefmCd0O5AQ11';
    window.open(stripeLink, '_blank');
    setShowPremiumPricing(false);
  };

  // Remove "Made with Emergent" branding
  useEffect(() => {
    const removeBranding = () => {
      // More targeted removal - only elements that exactly match branding patterns
      const elements = document.querySelectorAll('*');
      elements.forEach(el => {
        const text = el.textContent?.trim();
        if (text && (
          text === 'Made with Emergent' || 
          text.startsWith('Made with Emergent') ||
          (text.includes('Made with') && text.includes('Emergent'))
        )) {
          // Only remove if it's a small element (likely branding, not content)
          if (el.offsetHeight < 50 && el.offsetWidth < 200) {
            el.style.display = 'none';
          }
        }
      });

      // Target specific branding selectors that won't affect main content
      const brandingSelectors = [
        'div[style*="position: fixed"][style*="bottom: 10px"]',
        'div[style*="position: fixed"][style*="right: 10px"]',
        'a[href*="emergent.ai"]',
        '*[class*="branding"]',
        '*[id*="branding"]'
      ];

      brandingSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.style.display = 'none';
        });
      });
    };

    // Run after a delay to let the page load first
    const timeout = setTimeout(removeBranding, 2000);
    const interval = setInterval(removeBranding, 5000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const [formData, setFormData] = useState({
    startingLocation: '',
    tripDuration: '3 Days',
    travelDates: '',
    groupSize: '2 People',
    budgetRange: 'Moderate',
    cuisineTypes: [],
    dietaryRestrictions: '',
    experienceLevel: 'Moderate',
    accommodationType: '',
    transportationMode: '',
    specialRequests: '',
    contactEmail: '',
    contactPhone: ''
  });

  const totalSteps = 4;

  const handleInputChange = (name, value) => {
    if (name === 'contactEmail') setEmailError('');
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value) 
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goHome = () => {
    setShowWizard(false);
    setCurrentStep(1);
  };

  const startWizard = () => {
    const remaining = getRemainingFreeItineraries();
    
    if (remaining > 0) {
      setShowWizard(true);
      setCurrentStep(1);
    } else {
      // Show Premium pricing modal directly when free limit reached
      showPremiumModal();
    }
  };

  // ——— "Add to Trip" picks (client-side, persisted) ———
  const ALL_DIRECTORY = [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS];
  const toggleTripPick = (rid) => {
    setTripPicks((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      localStorage.setItem('culinera_trip_picks', JSON.stringify([...next]));
      setToast(next.has(rid) ? 'Added to your trip' : 'Removed from your trip');
      setTimeout(() => setToast(''), 1600);
      return next;
    });
  };
  const clearTripPicks = () => {
    setTripPicks(new Set());
    localStorage.setItem('culinera_trip_picks', '[]');
  };
  const planTripFromPicks = () => {
    // Seed the wizard's starting city from the most common city among picks.
    const cities = [...tripPicks]
      .map((id) => ALL_DIRECTORY.find((r) => r.id === id))
      .filter(Boolean)
      .map((r) => r.city);
    const topCity = cities.sort((a, b) =>
      cities.filter((c) => c === b).length - cities.filter((c) => c === a).length
    )[0];
    if (topCity) setFormData((prev) => ({ ...prev, startingLocation: topCity, mustInclude: [...tripPicks] }));
    else setFormData((prev) => ({ ...prev, mustInclude: [...tripPicks] }));
    setShowRestaurants(false);
    startWizard();
  };

  const submitForm = async () => {
    // 0) Email is required before generating or spending a credit
    const email = (formData.contactEmail || '').trim();
    if (!email) { setEmailError('Please enter your email to receive your itinerary.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { setEmailError('Please enter a valid email address (e.g. you@example.com).'); return; }
    if (getRemainingFreeItineraries() <= 0) { setShowWizard(false); setShowPremiumPricing(true); return; }
    setEmailError('');

    // 1) Build itinerary on-site immediately (fast, works offline too)
    const itinerary = generateItinerary(formData);
    setGeneratedItinerary(itinerary);
    setSavedItineraryId(null);

    // 2) Email it as a bonus (non-blocking)
    if (formData.contactEmail) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-itinerary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
      } catch (e) {
        console.warn('Email send failed (non-blocking):', e);
      }
    }

    // 3) Auto-save for logged-in users
    if (isAuthenticated) {
      try {
        const saved = await apiFetch('/api/itineraries', {
          method: 'POST',
          body: {
            title: itinerary.title,
            startingLocation: formData.startingLocation,
            tripDuration: formData.tripDuration,
            travelDates: formData.travelDates,
            groupSize: formData.groupSize,
            budgetRange: formData.budgetRange,
            cuisineTypes: formData.cuisineTypes,
            dietaryRestrictions: formData.dietaryRestrictions,
            experienceLevel: formData.experienceLevel,
            accommodationType: formData.accommodationType,
            transportationMode: formData.transportationMode,
            specialRequests: formData.specialRequests,
            contactEmail: formData.contactEmail,
            restaurants: itinerary.restaurants,
            days: itinerary.days,
          },
        });
        setSavedItineraryId(saved.id);
      } catch (e) {
        console.warn('Auto-save failed:', e);
      }
    }

    incrementFreeUsage();
    setShowWizard(false);
    setCurrentStep(1);
  };

  const saveCurrentItineraryManually = async () => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    if (!generatedItinerary || savedItineraryId) return;
    try {
      const saved = await apiFetch('/api/itineraries', {
        method: 'POST',
        body: {
          title: generatedItinerary.title,
          startingLocation: generatedItinerary.summary?.location || formData.startingLocation,
          tripDuration: generatedItinerary.summary?.duration || formData.tripDuration,
          restaurants: generatedItinerary.restaurants,
          days: generatedItinerary.days,
        },
      });
      setSavedItineraryId(saved.id);
      setToast('Saved to your profile');
    } catch (e) { setToast(e.message || 'Save failed'); }
  };

  const emailCurrentItinerary = async () => {
    const target = formData.contactEmail || user?.email;
    if (!target) { setShowAuthModal(true); return; }
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-itinerary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, contactEmail: target }),
      });
      setToast(`Emailed a copy to ${target}`);
    } catch { setToast('Email failed. Please try again.'); }
  };

  const toggleFavorite = async (rid) => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    const next = new Set(favoriteIds);
    if (next.has(rid)) {
      next.delete(rid);
      setFavoriteIds(next);
      try { await apiFetch(`/api/favorites/${rid}`, { method: 'DELETE' }); } catch {}
    } else {
      next.add(rid);
      setFavoriteIds(next);
      try { await apiFetch('/api/favorites', { method: 'POST', body: { restaurant_id: rid } }); } catch {}
    }
  };

  const handleSurprise = (preset) => {
    const filled = { ...formData, ...preset };
    setFormData(filled);
    const itinerary = generateItinerary(filled);
    setGeneratedItinerary(itinerary);
    setSavedItineraryId(null);
    setShowSpinWheel(false);
  };

  const handleTweakFirst = (preset) => {
    setFormData({ ...formData, ...preset });
    setShowSpinWheel(false);
    setShowWizard(true);
    setCurrentStep(1);
  };

  // ——— Top-level view router (simple state-based) ———
  if (showProfile && isAuthenticated) {
    return (
      <ProfileView
        onBack={() => setShowProfile(false)}
        onOpenItinerary={(trip) => {
          // Rebuild the display-friendly itinerary shape from saved data
          const restaurants = trip.restaurants || [];
          const days = trip.days && trip.days.length ? trip.days : [{ day: 1, meals: restaurants.slice(0, 3).map((r, i) => ({ slot: ['Breakfast','Lunch','Dinner'][i] || 'Meal', restaurant: r })) }];
          setGeneratedItinerary({
            title: trip.title,
            duration: parseInt(trip.tripDuration) || 3,
            restaurants,
            foodTrucks: [],
            days,
            summary: {
              location: trip.startingLocation,
              duration: trip.tripDuration,
              groupSize: trip.groupSize,
              budget: trip.budgetRange,
              cuisines: trip.cuisineTypes || [],
            },
          });
          setSavedItineraryId(trip.id);
          setShowProfile(false);
        }}
        onShowPremiumModal={showPremiumModal}
      />
    );
  }

  if (generatedItinerary) {
    return (
      <>
        <ItineraryView
          itinerary={generatedItinerary}
          user={user}
          saved={!!savedItineraryId}
          savedItineraryId={savedItineraryId}
          onSave={saveCurrentItineraryManually}
          onEmail={emailCurrentItinerary}
          onBack={() => { setGeneratedItinerary(null); setSavedItineraryId(null); }}
        />
        {toast && (
          <div data-testid="toast" className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">{toast}</div>
        )}
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </>
    );
  }

  // If showing Learn More demo, display it
  if (showLearnMore) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Culinera</span>
              </div>
              <button 
                onClick={() => setShowLearnMore(false)}
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">Culinera</span> Works
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Creating personalized Alberta culinary adventures has never been easier. Here's how we turn your food dreams into reality.
            </p>
          </div>

          {/* Step by Step Process */}
          <div className="space-y-16">
            {/* Step 1 */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/2">
                <div className="bg-teal-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <span className="text-teal-600 font-bold text-xl">1</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Tell Us Your Preferences</h3>
                <p className="text-gray-600 mb-6">
                  Use our smart 4-step wizard to share your travel details, cuisine preferences, budget range, and dietary needs. 
                  The more you tell us, the better we can personalize your journey!
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>✓ Starting location & travel dates</li>
                  <li>✓ Group size & budget preferences</li>
                  <li>✓ Cuisine types & dietary restrictions</li>
                  <li>✓ Experience level & special requests</li>
                </ul>
              </div>
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="bg-teal-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-teal-800">Trip Planning Wizard</h4>
                      <span className="text-xs text-teal-600">Step 1 of 4</span>
                    </div>
                    <div className="w-full bg-teal-200 rounded-full h-2 mb-3">
                      <div className="bg-teal-600 h-2 rounded-full w-1/4"></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Starting Location</label>
                      <select className="w-full p-2 border rounded text-sm">
                        <option>Calgary</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Budget Range</label>
                      <div className="flex gap-2 mt-1">
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded text-xs">$$ Moderate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
              <div className="lg:w-1/2">
                <div className="bg-cyan-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <span className="text-cyan-600 font-bold text-xl">2</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">AI-Powered Matching</h3>
                <p className="text-gray-600 mb-6">
                  Our intelligent system analyzes your preferences against our curated database of 100+ Alberta restaurants, 
                  food trucks, wineries, and hidden culinary gems to find perfect matches.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>✓ Location-based recommendations</li>
                  <li>✓ Budget-appropriate suggestions</li>
                  <li>✓ Cuisine preference filtering</li>
                  <li>✓ Seasonal availability consideration</li>
                </ul>
              </div>
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h4 className="font-semibold text-gray-800 mb-4">🧠 Smart Matching Algorithm</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded border-l-4 border-green-400">
                      <span className="text-sm">Fine Dining + Calgary + $$$$</span>
                      <span className="text-green-600 font-bold">✓ EIGHT</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                      <span className="text-sm">Food Trucks + Budget + Asian</span>
                      <span className="text-blue-600 font-bold">✓ Taiko Taco</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded border-l-4 border-purple-400">
                      <span className="text-sm">Indigenous + Premium</span>
                      <span className="text-purple-600 font-bold">✓ Bernadette's</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/2">
                <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <span className="text-purple-600 font-bold text-xl">3</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Receive Your Custom Itinerary</h3>
                <p className="text-gray-600 mb-6">
                  Within seconds, get a beautifully formatted email with your personalized culinary itinerary. 
                  Includes restaurant details, daily suggestions, and insider tips for the perfect food adventure.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>✓ Day-by-day meal planning</li>
                  <li>✓ Restaurant contact & reservation info</li>
                  <li>✓ Driving routes & food truck locations</li>
                  <li>✓ Seasonal specialties & must-try dishes</li>
                </ul>
              </div>
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-4">
                  <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-4 rounded-t-lg">
                    <h4 className="font-bold">🍽️ Your Alberta Culinary Journey</h4>
                    <p className="text-sm opacity-90">Personalized Itinerary Created Just for You</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="bg-teal-50 p-3 rounded">
                      <h5 className="font-semibold text-teal-800">Your Trip Summary</h5>
                      <div className="text-xs text-teal-700 mt-1">
                        <div>📍 Calgary • 3 Days • $$ Moderate</div>
                      </div>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 mb-2">🍽️ Recommended Restaurants</h5>
                      <div className="space-y-2">
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <strong>EIGHT</strong> - Exclusive tasting menu ⭐⭐⭐⭐⭐
                        </div>
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          <strong>Bernadette's</strong> - Indigenous cuisine ⭐⭐⭐⭐⭐
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">📅 Daily Itinerary</h5>
                      <div className="bg-purple-50 p-2 rounded text-xs mt-1">
                        <strong>Day 1:</strong> Arrival + Fine dining at EIGHT
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
              <div className="lg:w-1/2">
                <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <span className="text-orange-600 font-bold text-xl">4</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore & Enjoy</h3>
                <p className="text-gray-600 mb-6">
                  Armed with your personalized itinerary, embark on an unforgettable Alberta culinary adventure! 
                  Discover hidden gems, taste amazing dishes, and create delicious memories.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>✓ Make reservations using provided contacts</li>
                  <li>✓ Follow suggested routes for efficiency</li>
                  <li>✓ Try recommended signature dishes</li>
                  <li>✓ Share your experience on social media</li>
                </ul>
              </div>
              <div className="lg:w-1/2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg shadow p-4 text-center">
                    <div className="text-2xl mb-2">🍴</div>
                    <div className="text-lg font-bold text-teal-600">135+</div>
                    <div className="text-xs text-gray-600">Fine Restaurants</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4 text-center">
                    <div className="text-2xl mb-2">🚚</div>
                    <div className="text-lg font-bold text-cyan-600">30+</div>
                    <div className="text-xs text-gray-600">Food Trucks</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4 text-center">
                    <div className="text-2xl mb-2">🍷</div>
                    <div className="text-lg font-bold text-purple-600">48</div>
                    <div className="text-xs text-gray-600">Cities &amp; Towns</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4 text-center">
                    <div className="text-2xl mb-2">✨</div>
                    <div className="text-lg font-bold text-orange-600">1000+</div>
                    <div className="text-xs text-gray-600">Combinations</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-lg p-8 text-white">
              <h3 className="text-3xl font-bold mb-4">Ready to Start Your Culinary Adventure?</h3>
              <p className="text-xl mb-6 opacity-90">Join thousands of food lovers who've discovered Alberta's hidden culinary gems</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={startWizard}
                  className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Start Your Journey Now
                </button>
                <button 
                  onClick={showPremiumModal}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Upgrade to Premium
                </button>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Is Culinera free to use?</h4>
                <p className="text-gray-600 text-sm">Yes! Everyone gets 3 free personalized itineraries. After that, upgrade to Premium ($9.99/month) for unlimited culinary adventures.</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-900 mb-2">How accurate are the recommendations?</h4>
                <p className="text-gray-600 text-sm">Our database is regularly updated with current restaurant information to ensure accuracy.</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Can I modify my itinerary?</h4>
                <p className="text-gray-600 text-sm">Absolutely! Run the wizard again with different preferences to get a new personalized itinerary anytime.</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Do you help with reservations?</h4>
                <p className="text-gray-600 text-sm">Premium members get assistance with reservations and access to exclusive booking slots at partner restaurants.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // If showing restaurant list, display it
  if (showRestaurants) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Culinera</span>
              </div>
              <button 
                onClick={() => setShowRestaurants(false)}
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </header>

        {/* Restaurant Directory */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Alberta's Best <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">Restaurants</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover exceptional dining experiences across Alberta, from hidden gems to award-winning establishments.
            </p>
          </div>

          {/* ——— Featured this week ——— */}
          {(() => {
            const featuredIds = ['next-of-kin', 'nero-yeg', 'mssm', 'proof-yyc', 'model-milk', 'two-penny', 'betty-lous-library', 'river-cafe'];
            const week = Math.floor(Date.now() / (7 * 86400000));
            const featured = featuredIds
              .map((id) => ALL_DIRECTORY.find((r) => r.id === id))
              .filter(Boolean);
            const rotated = featured.map((_, i) => featured[(i + (week % featured.length)) % featured.length]);
            if (rotated.length === 0) return null;
            return (
              <div className="mb-12" data-testid="featured-carousel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="text-teal-600">✦</span> Featured this week
                  </h2>
                  <span className="text-sm text-gray-400 hidden sm:block">Scroll →</span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory">
                  {rotated.map((r) => (
                    <a
                      key={r.id}
                      href={`/restaurants/${r.id}`}
                      data-testid={`featured-card-${r.id}`}
                      className="group relative shrink-0 w-72 h-44 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all snap-start"
                    >
                      <img src={getRestaurantImage(r, 800)} alt={r.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                      {isTrending(r.id) && (
                        <span className="absolute top-3 right-3 text-[11px] font-bold bg-amber-400 text-amber-950 px-2.5 py-1 rounded-full shadow-sm">✦ Trending</span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[11px] font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">{r.cuisine}</span>
                          <span className="text-[11px] font-semibold bg-white/90 text-gray-900 px-2 py-0.5 rounded-full">{r.price}</span>
                        </div>
                        <div className="text-white font-bold text-lg leading-tight">{r.name}</div>
                        <div className="text-white/70 text-xs">{r.neighborhood ? `${r.neighborhood}, ` : ''}{r.city}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* City Filter Tabs */}
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-lg p-2 shadow-md overflow-x-auto max-w-full flex gap-1" role="tablist" aria-label="Filter by city">
              {ALBERTA_CITIES.map((city) => (
                <button
                  key={city}
                  data-testid={`city-tab-${city.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setCityFilter(city)}
                  role="tab"
                  aria-selected={cityFilter === city}
                  className={`px-4 py-2 rounded-md font-semibold whitespace-nowrap transition-colors ${
                    cityFilter === city
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-600 hover:text-teal-600'
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>

          {/* Search + Price Filter */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-8 max-w-3xl mx-auto">
            <input
              data-testid="restaurant-search-input"
              type="search"
              placeholder="Search restaurants, cuisines, or tags…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              aria-label="Search restaurants"
            />
            <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm" role="group" aria-label="Filter by price">
              {['All', '$', '$$', '$$$', '$$$$'].map((p) => (
                <button
                  key={p}
                  data-testid={`price-filter-${p.replace(/\$/g, 'd') || 'all'}`}
                  onClick={() => setPriceFilter(p)}
                  className={`px-3 py-2 rounded font-semibold text-sm transition-colors ${
                    priceFilter === p
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-600 hover:text-teal-600'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              data-testid="trending-filter-chip"
              onClick={() => setTrendingOnly((v) => !v)}
              aria-pressed={trendingOnly}
              className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-colors border ${
                trendingOnly
                  ? 'bg-amber-400 text-amber-950 border-amber-400 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700'
              }`}
            >
              ✦ Trending <span data-testid="trending-count" className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${trendingOnly ? 'bg-amber-950/15 text-amber-950' : 'bg-amber-100 text-amber-700'}`}>{ALL_DIRECTORY.filter((r) => isTrending(r.id)).length} new</span>{trendingOnly ? ' ✓' : ''}
            </button>
            <select
              data-testid="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-white text-gray-700 shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
              aria-label="Sort restaurants"
            >
              <option value="recommended">Sort: Recommended</option>
              <option value="newest">Sort: Newest first</option>
            </select>
          </div>

          {/* Restaurant + Truck Count */}
          <p className="text-center text-gray-600 mb-3" data-testid="restaurant-count">
            Showing <span className="font-semibold text-teal-700">{filteredRestaurants.length}</span> of {ALBERTA_RESTAURANTS.length + ALBERTA_FOOD_TRUCKS.length + communityTrucks.length + dynamicRestaurants.length} restaurants &amp; food trucks
          </p>
          <p className="text-center text-sm mb-6">
            <a href="/submit-truck" data-testid="submit-truck-link" className="text-teal-600 hover:underline font-semibold">🚚 Run a food truck? Add it to the directory →</a>
          </p>

          {/* Restaurant Grid */}
          {filteredRestaurants.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow-sm max-w-xl mx-auto">
              <p className="text-xl font-semibold text-gray-800 mb-2">No restaurants match these filters.</p>
              <p className="text-gray-600 mb-4">Try a different city or clear the search box.</p>
              <button
                data-testid="reset-filters-btn"
                onClick={() => { setCityFilter('All'); setPriceFilter('All'); setSearchQuery(''); }}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(sortBy === 'newest'
                ? [...filteredRestaurants].sort((a, b) => (isTrending(b.id) ? 1 : 0) - (isTrending(a.id) ? 1 : 0))
                : filteredRestaurants
              ).map((r) => {
                const priceClass = r.price === '$'
                  ? 'bg-green-100 text-green-800'
                  : r.price === '$$'
                  ? 'bg-yellow-100 text-yellow-800'
                  : r.price === '$$$'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-red-100 text-red-800';
                return (
                  <article
                    key={r.id}
                    data-testid={`restaurant-card-${r.id}`}
                    className="group bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                    itemScope
                    itemType="https://schema.org/Restaurant"
                  >
                    <a href={`/restaurants/${r.id}`} aria-label={r.name} className="relative block h-52 overflow-hidden">
                      <img
                        src={getRestaurantImage(r, 800)}
                        alt={`${r.name} — ${r.cuisine} in ${r.city}, Alberta`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        itemProp="image"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <span className={`absolute top-3 left-3 text-xs font-bold ${priceClass} px-2.5 py-1 rounded-full shadow-sm`} itemProp="priceRange">{r.price}</span>
                      <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                        {isNew(r.id) && (
                          <span data-testid={`new-ribbon-${r.id}`} className="text-[11px] font-extrabold bg-rose-600 text-white px-2.5 py-1 rounded-full shadow-sm tracking-wide">● NEW</span>
                        )}
                        {isTrending(r.id) && (
                          <span data-testid={`trending-badge-${r.id}`} className="text-[11px] font-bold bg-amber-400 text-amber-950 px-2.5 py-1 rounded-full shadow-sm">✦ Trending</span>
                        )}
                      </div>
                      <span className="absolute bottom-3 left-3 text-xs font-semibold bg-white/90 backdrop-blur text-gray-800 px-2.5 py-1 rounded-full" itemProp="servesCuisine">{r.cuisine}</span>
                      {r.isFoodTruck && (
                        <span className="absolute bottom-3 right-3 text-xs font-bold bg-cyan-500 text-white px-2.5 py-1 rounded-full shadow-sm">🚚 Food Truck</span>
                      )}
                    </a>
                    <div className="p-5 relative flex flex-col flex-1">
                      <button
                        data-testid={`fav-btn-${r.id}`}
                        onClick={() => toggleFavorite(r.id)}
                        className={`absolute -top-6 right-4 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-xl transition-all hover:scale-110 ${favoriteIds.has(r.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                        aria-label={favoriteIds.has(r.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >{favoriteIds.has(r.id) ? '♥' : '♡'}</button>

                      <a
                        href={`/restaurants/${r.id}`}
                        data-testid={`restaurant-link-${r.id}`}
                        className="text-lg font-bold text-gray-900 hover:text-teal-700 transition-colors pr-8 leading-tight"
                        itemProp="name"
                      >{r.name}</a>

                      <p className="text-gray-500 text-sm mt-1 mb-2 flex items-center gap-1">
                        <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                          <span itemProp="addressLocality">{r.city}</span>{r.neighborhood ? `, ${r.neighborhood}` : ''}, <span itemProp="addressRegion">AB</span>
                        </span>
                      </p>

                      {r.isFoodTruck && (r.instagram || r.twitter) && (
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          {r.instagram && (
                            <a
                              href={`https://instagram.com/${r.instagram}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`ig-${r.id}`}
                              className="text-xs bg-gradient-to-r from-pink-500 to-yellow-500 text-white px-2 py-1 rounded-full font-semibold hover:opacity-90"
                              title="See today's location on Instagram"
                            >📍 @{r.instagram}</a>
                          )}
                          {r.twitter && (
                            <a
                              href={`https://twitter.com/${r.twitter}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`x-${r.id}`}
                              className="text-xs bg-black text-white px-2 py-1 rounded-full font-semibold hover:opacity-90"
                              title="See today's location on X"
                            >𝕏 @{r.twitter}</a>
                          )}
                        </div>
                      )}

                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3 flex-1" itemProp="description">{r.description}</p>

                      <button
                        data-testid={`add-trip-btn-${r.id}`}
                        onClick={() => toggleTripPick(r.id)}
                        className={`w-full mb-3 py-2 rounded-lg text-sm font-semibold transition-colors border ${
                          tripPicks.has(r.id)
                            ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                            : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'
                        }`}
                      >
                        {tripPicks.has(r.id) ? '✓ In your trip' : '＋ Add to trip'}
                      </button>

                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">★ Culinera Pick</span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {r.tags.slice(0, 2).map((t) => (
                            <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* ——— Food Trails ——— */}
          <section className="mt-20" data-testid="food-trails-section">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Alberta Food Trails</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Ready-made crawls through the province's best neighbourhoods. Follow one stop to stop, or turn it into a full itinerary in one click.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FOOD_TRAILS.map((trail) => {
                const stops = trail.stops
                  .map((id) => ALL_DIRECTORY.find((r) => r.id === id))
                  .filter(Boolean);
                if (stops.length === 0) return null;
                return (
                  <div
                    key={trail.id}
                    data-testid={`food-trail-${trail.id}`}
                    className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <a href={`/trails/${trail.id}`} data-testid={`view-trail-${trail.id}`} className="absolute inset-0 z-10" aria-label={`View ${trail.title}`}></a>
                      {savedTrailIds.has(trail.id) && (
                        <span data-testid={`trail-saved-${trail.id}`} className="absolute top-3 right-3 z-20 text-[11px] font-bold bg-teal-600 text-white px-2.5 py-1 rounded-full shadow-sm">Saved ✓</span>
                      )}
                      <div className="absolute inset-0 grid grid-cols-3">
                        {stops.slice(0, 3).map((r) => (
                          <img key={r.id} src={getRestaurantImage(r, 400)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-[11px] font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">{trail.city}</span>
                        <h3 className="text-white font-bold text-xl leading-tight mt-1">{trail.title}</h3>
                        <p className="text-white/80 text-xs">{trail.tagline}</p>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <p className="text-gray-600 text-sm mb-4">{trail.blurb}</p>
                      <ol className="space-y-1 mb-5 flex-1">
                        {stops.map((r, i) => (
                          <li key={r.id} className="flex items-center gap-2 text-sm">
                            <span className="w-5 h-5 shrink-0 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                            <a href={`/restaurants/${r.id}`} data-testid={`trail-stop-${r.id}`} className="text-gray-800 hover:text-teal-700 font-medium">{r.name}</a>
                            <span className="text-gray-400 text-xs">· {r.neighborhood || r.city}</span>
                          </li>
                        ))}
                      </ol>
                      <div className="flex gap-2">
                        <a
                          href={`/trails/${trail.id}`}
                          data-testid={`open-trail-${trail.id}`}
                          className="flex-1 text-center bg-white border border-teal-200 text-teal-700 py-2.5 rounded-xl font-semibold hover:bg-teal-50 transition-colors"
                        >
                          View trail
                        </a>
                        <button
                          data-testid={`plan-trail-${trail.id}`}
                          onClick={() => { setFormData((prev) => ({ ...prev, startingLocation: trail.city })); setShowRestaurants(false); startWizard(); }}
                          className="flex-1 bg-teal-600 text-white py-2.5 rounded-xl font-semibold hover:bg-teal-700 transition-colors"
                        >
                          Plan this trail →
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <div className="bg-teal-600 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Plan Your Culinary Journey?</h3>
              <p className="mb-6">Use our wizard to create a personalized itinerary featuring these amazing restaurants!</p>
              <button 
                onClick={startWizard}
                className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Start Planning Now
              </button>
            </div>
          </div>
        </section>

        {/* Floating trip tray */}
        {tripPicks.size > 0 && (
          <div data-testid="trip-tray" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-gray-900 text-white rounded-full shadow-2xl pl-5 pr-2 py-2 flex items-center gap-4 max-w-[92vw]">
            <span className="text-sm font-semibold whitespace-nowrap">
              {tripPicks.size} {tripPicks.size === 1 ? 'spot' : 'spots'} in your trip
            </span>
            <button data-testid="clear-trip-btn" onClick={clearTripPicks} className="text-xs text-gray-300 hover:text-white underline">Clear</button>
            <button
              data-testid="plan-trip-picks-btn"
              onClick={planTripFromPicks}
              className="bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold px-5 py-2 rounded-full whitespace-nowrap transition-colors"
            >
              Plan my trip →
            </button>
          </div>
        )}
      </div>
    );
  }
  if (showWizard) {
    return (
      <ItineraryWizard
        currentStep={currentStep}
        totalSteps={totalSteps}
        formData={formData}
        handleInputChange={handleInputChange}
        handleCheckboxChange={handleCheckboxChange}
        nextStep={nextStep}
        prevStep={prevStep}
        goHome={goHome}
        submitForm={submitForm}
        emailError={emailError}
      />
    );
  }

  // Main website homepage
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      {/* Stripe Success Banner */}
      {showPremiumSuccess && (
        <div
          data-testid="premium-success-banner"
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 text-center relative"
          role="status"
        >
          <span className="font-semibold">👑 Welcome to Culinera Premium! Your unlimited access is now active.</span>
          <button
            data-testid="close-success-banner"
            onClick={() => setShowPremiumSuccess(false)}
            className="absolute top-2 right-3 text-white/80 hover:text-white text-xl"
            aria-label="Dismiss welcome banner"
          >×</button>
        </div>
      )}

      {/* Admin link badge — only when admin has pending submissions */}
      {pendingSubmissions.length > 0 && (
        <div data-testid="admin-queue-banner" className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm">
          <a href="/admin" className="text-amber-900 font-semibold hover:underline">📥 You have {pendingSubmissions.length} pending truck submission{pendingSubmissions.length === 1 ? '' : 's'} — review them in the admin queue →</a>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <span className="text-xl font-bold text-gray-900">Culinera</span>
            </div>
            <div className="flex items-center gap-3">
              <a href="/blog" data-testid="header-blog-link" className="text-gray-600 hover:text-gray-900 font-semibold hidden sm:inline">Journal</a>
              {isAuthenticated && user?.is_admin && (
                <a
                  href="/admin"
                  data-testid="header-admin-link"
                  className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-800"
                >🛠 Admin</a>
              )}
              {isAuthenticated ? (
                <button
                  data-testid="header-profile-btn"
                  onClick={() => setShowProfile(true)}
                  aria-label="My Account"
                  title="My Account"
                  className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 text-white font-bold flex items-center justify-center text-sm">
                      {(user?.name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline font-semibold">{user?.name?.split(' ')[0] || 'Profile'}</span>
                </button>
              ) : (
                <button
                  data-testid="header-signin-btn"
                  onClick={() => setShowAuthModal(true)}
                  className="text-gray-600 hover:text-gray-900 font-semibold"
                >Sign in</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Discover Alberta's
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">Hidden Culinary Gems</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Create personalized 3-5 day culinary road trips featuring the best restaurants, 
            food trucks, wineries, and secret dining experiences across Alberta.
          </p>
          
          {/* Premium Status / Free Usage Indicator */}
          {isPremiumUser ? (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <p className="text-purple-800 font-semibold flex items-center justify-center">
                <span className="mr-2">👑</span> Premium Active - Unlimited Itineraries!
              </p>
              <p className="text-purple-600 text-sm text-center">Thank you for supporting Culinera</p>
            </div>
          ) : getRemainingFreeItineraries() > 0 ? (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <p className="text-teal-800 font-semibold">
                {`🎁 ${getRemainingFreeItineraries()} free ${getRemainingFreeItineraries() === 1 ? 'itinerary' : 'itineraries'} remaining`}
              </p>
              <p className="text-teal-600 text-sm">Then upgrade to Premium for unlimited access!</p>
            </div>
          ) : (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <p className="text-purple-800 font-semibold">🌟 Upgrade to Premium</p>
              <p className="text-purple-600 text-sm">Unlimited itineraries for just $9.99/month</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              data-testid="hero-start-journey-btn"
              onClick={startWizard}
              className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              Start Your Culinary Journey
            </button>
            <button 
              data-testid="hero-explore-restaurants-btn"
              onClick={showRestaurantList}
              className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              📍 Explore Restaurants
            </button>
            <button
              data-testid="hero-spin-wheel-btn"
              onClick={() => setShowSpinWheel(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              🎡 Spin the Wheel
            </button>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Not ready to plan yet?{" "}
            <a href="/sample-itineraries" data-testid="hero-sample-link" className="text-teal-600 font-semibold hover:underline">See a sample itinerary →</a>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
          <div className="text-center">
            <div className="text-3xl font-bold text-teal-600">135+</div>
            <div className="text-gray-600">Restaurants</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-cyan-600">30+</div>
            <div className="text-gray-600">Food Trucks</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">48</div>
            <div className="text-gray-600">Cities &amp; Towns</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">1000+</div>
            <div className="text-gray-600">Combinations</div>
          </div>
        </div>
      </section>

      {/* ——— New & Trending row (home) ——— */}
      {(() => {
        const trending = ALL_DIRECTORY.filter((r) => isTrending(r.id));
        if (trending.length === 0) return null;
        return (
          <section className="py-14 bg-gray-50" data-testid="home-trending-row">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="text-amber-500">✦</span> New &amp; Trending in Alberta
                  </h2>
                  <p className="text-gray-600 mt-1">The freshest openings and buzziest bars our editors are loving right now.</p>
                </div>
                <button
                  data-testid="trending-see-all"
                  onClick={showRestaurantList}
                  className="hidden sm:inline text-teal-600 font-semibold hover:text-teal-700 whitespace-nowrap"
                >See all →</button>
              </div>
              <div className="flex gap-5 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory">
                {trending.map((r) => (
                  <a
                    key={r.id}
                    href={`/restaurants/${r.id}`}
                    data-testid={`home-trending-card-${r.id}`}
                    className="group relative shrink-0 w-64 h-80 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all snap-start"
                  >
                    <img src={getRestaurantImage(r, 700)} alt={r.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                    <span className="absolute top-3 left-3 text-[11px] font-bold bg-amber-400 text-amber-950 px-2.5 py-1 rounded-full shadow-sm">✦ Trending</span>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="text-[11px] font-semibold bg-white/90 text-gray-900 px-2 py-0.5 rounded-full">{r.cuisine}</span>
                      <div className="text-white font-bold text-lg leading-tight mt-2">{r.name}</div>
                      <div className="text-white/75 text-xs">{r.neighborhood ? `${r.neighborhood}, ` : ''}{r.city}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        );
      })()}


      {/* Premium Section */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, honest pricing</h2>
          <p className="text-xl text-gray-600 mb-10">Start free. Upgrade only when you want unlimited trips.</p>

          <div className="grid md:grid-cols-2 gap-6 text-left max-w-3xl mx-auto mb-10">
            {/* Free tier */}
            <div data-testid="pricing-free-card" className="border border-gray-200 rounded-2xl p-7 bg-gray-50">
              <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Free</div>
              <div className="mt-2 mb-1"><span className="text-4xl font-bold text-gray-900">$0</span></div>
              <p className="text-sm text-gray-500 mb-5">No card required</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> 3 personalized itineraries</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Full 166+ restaurant directory</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Save favorites &amp; share trips</li>
              </ul>
            </div>
            {/* Premium tier */}
            <div data-testid="pricing-premium-card" className="border-2 border-purple-500 rounded-2xl p-7 bg-purple-50 relative">
              <span className="absolute -top-3 right-6 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">Most popular</span>
              <div className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Premium</div>
              <div className="mt-2 mb-1"><span className="text-4xl font-bold text-gray-900">$9.99</span><span className="text-gray-500 font-medium"> / month</span></div>
              <p className="text-sm text-gray-500 mb-5">Cancel anytime</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="text-purple-600 mr-2 font-bold">✓</span> <span><strong>Unlimited</strong>{' '}itineraries</span></li>
                <li className="flex items-start"><span className="text-purple-600 mr-2 font-bold">✓</span> Saved trips synced across every device</li>
                <li className="flex items-start"><span className="text-purple-600 mr-2 font-bold">✓</span> Priority email support from a real human</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              data-testid="premium-upgrade-btn"
              onClick={showPremiumModal}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Upgrade to Premium — $9.99/mo
            </button>
            <button 
              data-testid="learn-more-btn"
              onClick={showLearnMoreDemo}
              className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Featured Restaurants */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Featured Alberta Culinary Experiences</h2>
          <p className="text-xl text-gray-600 text-center mb-12">From exclusive fine dining to hidden food trucks, discover the diverse flavors of Alberta</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* EIGHT Restaurant */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold">EIGHT</h3>
                  <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded">$$$$</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Calgary</p>
                <p className="text-gray-700 mb-4">A highly exclusive chef-driven tasting menu experience limited to just eight guests per seating. Expect innovative, multicultural dishes crafted with precision and care.</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded font-semibold">Culinera Pick</span>
                  <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">Fine Dining</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">Hidden Gem</span>
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Exclusive</span>
                </div>
              </div>
            </div>

            {/* Azuridge */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold">Azuridge – Opal Dining</h3>
                  <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">$$$</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Priddis</p>
                <p className="text-gray-700 mb-4">A scenic and tranquil fine dining retreat nestled in the foothills. Elegant dishes like Alberta beef tenderloin and mahi-mahi complemented by curated wine selections.</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded font-semibold">Culinera Pick</span>
                  <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">Fine Dining</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Scenic Views</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Foothills Location</span>
                </div>
              </div>
            </div>

            {/* Bernadette's */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-semibold">Bernadette's</h3>
                  <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">$$$</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">Edmonton</p>
                <p className="text-gray-700 mb-4">Led by Chef Scott Iserhoff (Omushkegowuk Cree), the restaurant offers refined dishes that honour family recipes and Northern culinary traditions.</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded font-semibold">Culinera Pick</span>
                  <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded">Indigenous</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Indigenous-Owned</span>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Cultural Experience</span>
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">Hidden Gem</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Food Trucks */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Street Food Adventures</h2>
          <p className="text-xl text-gray-600 text-center mb-12">Discover Alberta's vibrant food truck scene with authentic flavors on wheels</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Family Squeezed</h3>
              <p className="text-gray-600 text-sm mb-1">Calgary</p>
              
              <p className="text-gray-700 text-sm mb-2">Fresh juice and smoothie truck serving healthy beverages across Calgary.</p>
              <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">Juice Bar</span>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Taiko Taco</h3>
              <p className="text-gray-600 text-sm mb-1">Calgary</p>
              
              <p className="text-gray-700 text-sm mb-2">Asian-fusion tacos bringing unique flavors to Calgary's food truck scene.</p>
              <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded">Asian Fusion</span>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">The Dumpling Hero</h3>
              <p className="text-gray-600 text-sm mb-1">Calgary</p>
              
              <p className="text-gray-700 text-sm mb-2">Specialty dumpling truck serving authentic handmade dumplings.</p>
              <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Chinese</span>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">The Hungry Beast</h3>
              <p className="text-gray-600 text-sm mb-1">Red Deer</p>
              
              <p className="text-gray-700 text-sm mb-2">Food truck dedicated to bringing the finest mobile cuisine in Central Alberta.</p>
              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">Mobile Cuisine</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Adsense Banner removed pending real AdSense approval. */}

      {/* CTA Section */}
      <section className="bg-teal-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">Ready for Your Culinary Adventure?</h2>
          <p className="text-xl text-teal-100 mb-8">Let our intelligent wizard create a personalized dining experience just for you</p>
          <button 
            data-testid="cta-start-planning-btn"
            onClick={startWizard}
            className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Start Planning Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* ——— My Account Modal ——— */}
      {showAccountModal && (
        <div
          data-testid="account-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-modal-title"
          onClick={() => setShowAccountModal(false)}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 id="account-modal-title" className="text-xl font-bold text-gray-900">My Account</h3>
              <button data-testid="close-account-modal" onClick={() => setShowAccountModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Close">×</button>
            </div>
            {isPremiumUser ? (
              <div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-purple-800 font-semibold">👑 Premium Member</p>
                  <p className="text-purple-600 text-sm">You have unlimited itineraries, saved trips on every device, and priority email support.</p>
                </div>
                <p className="text-sm text-gray-600 mb-4">Need to update billing or cancel? Email <a className="text-teal-600 hover:underline" href="mailto:hello@culinera.ca">hello@culinera.ca</a> and we'll take care of it.</p>
                <button
                  data-testid="account-close-btn"
                  onClick={() => setShowAccountModal(false)}
                  className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-teal-700"
                >Close</button>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-2">You're on the <strong>Free Plan</strong>.</p>
                <p className="text-gray-600 mb-4">
                  {`${getRemainingFreeItineraries()} free ${getRemainingFreeItineraries() === 1 ? 'itinerary' : 'itineraries'} remaining. Upgrade anytime to unlock unlimited access.`}
                </p>
                <div className="flex gap-3">
                  <button
                    data-testid="account-upgrade-btn"
                    onClick={() => { setShowAccountModal(false); showPremiumModal(); }}
                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700"
                  >Upgrade to Premium</button>
                  <button
                    data-testid="account-close-btn"
                    onClick={() => setShowAccountModal(false)}
                    className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50"
                  >Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ——— Premium Pricing Modal ——— */}
      {showPremiumPricing && (
        <div
          data-testid="premium-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="premium-modal-title"
          onClick={() => setShowPremiumPricing(false)}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 id="premium-modal-title" className="text-2xl font-bold text-gray-900">Culinera Premium</h3>
              <button data-testid="close-premium-modal" onClick={() => setShowPremiumPricing(false)} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Close">×</button>
            </div>
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg p-5 mb-4 text-center">
              <div className="text-4xl font-bold">$9.99<span className="text-lg font-normal opacity-80">/month</span></div>
              <div className="text-sm opacity-90 mt-1">Cancel anytime • Secure checkout via Stripe</div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700 mb-5">
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> UNLIMITED personalized itineraries</li>
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Saved trips synced across every device</li>
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Unlimited favorite restaurants</li>
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Shareable public trip links with social-ready preview images</li>
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Priority email support from a real human (hello@culinera.ca)</li>
              <li className="flex items-start"><span className="text-teal-600 mr-2 font-bold">✓</span> Help us grow — your support funds new Alberta cities &amp; restaurants</li>
            </ul>
            <button
              data-testid="proceed-to-stripe-btn"
              onClick={proceedToStripeCheckout}
              className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Continue to Secure Checkout →
            </button>
            <p className="text-xs text-gray-500 text-center mt-3">You'll be taken to Stripe to complete your purchase. You'll return here automatically after payment.</p>
          </div>
        </div>
      )}
      {/* ——— Auth Modal ——— */}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      {/* ——— Spin the Wheel ——— */}
      {showSpinWheel && (
        <SpinWheel
          onClose={() => setShowSpinWheel(false)}
          onSurprise={handleSurprise}
          onTweak={handleTweakFirst}
        />
      )}

      {/* ——— Toast ——— */}
      {toast && (
        <div data-testid="toast" className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">{toast}</div>
      )}
    </div>
  );
};

export default CulinaraApp;
